import React, { useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import { HorizontalStack } from '@ztechapps/webui.util'
import type { FeatureFlagKeyEnum } from '@ztechapps/common.contract'
import { humanize } from '@ztechapps/common.utils'

export type FeatureFlagChoiceSelectProps<T extends string = string> = {
  featureKey: FeatureFlagKeyEnum
  featureName?: string
  description?: string
  selectedChoice: T
  choices: readonly T[]
  metadata: Record<T, { name: string; description: string }>
  onChoiceChange: (choice: T) => void
  loading?: boolean
}

export const FeatureFlagChoiceSelect = <T extends string = string>({
  featureKey,
  featureName,
  description,
  selectedChoice,
  choices,
  metadata,
  onChoiceChange,
  loading = false,
}: FeatureFlagChoiceSelectProps<T>) => {
  const [internalChoice, setInternalChoice] = useState<T | null>(null)

  const displayChoice = internalChoice ?? selectedChoice
  const isUpdating = loading

  const handleSelect = (choice: T) => {
    setInternalChoice(choice)
    onChoiceChange(choice)
  }

  const currentMetadata = metadata[displayChoice] || {
    name: humanize(displayChoice),
    description: '',
  }

  return (
    <HorizontalStack gap={3} className="justify-content-between align-items-start">
      <div className="flex-fill">
        <h5 className="mb-1">{featureName ?? humanize(featureKey)}</h5>
        <p className="text-muted mb-0 small">{description ?? currentMetadata.description}</p>
        {currentMetadata.description && (
          <p className="text-muted mb-0 small mt-1">
            <strong>Selected: {currentMetadata.name}</strong>
          </p>
        )}
      </div>
      <div style={{ minWidth: '150px' }}>
        <Dropdown>
          <Dropdown.Toggle variant="outline-secondary" disabled={isUpdating} className="w-100 text-start">
            {currentMetadata.name}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {choices.map((choice) => {
              const choiceMetadata = metadata[choice] || { name: humanize(choice), description: '' }
              return (
                <Dropdown.Item key={choice} active={choice === displayChoice} onClick={() => handleSelect(choice)}>
                  {choiceMetadata.name}
                </Dropdown.Item>
              )
            })}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </HorizontalStack>
  )
}
